import { Component, Input, HostBinding, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs/internal/Subscription';
import { RouteHelperService } from '@app/shared/services/route-helper.service';
import { NotificationStateAction } from '../../../../shared/models/notification-state-action.model';
import { NotificationItemApiModel } from '@app/api/api-models/notifications/NotificationItemApiModel';
import { CustomToastersService, StartQuizService } from 'src/app/shared/services';
import { NavNotificationsService } from '@app/api/api-services/nav-notifications.service';
import { of } from 'rxjs';
import { NotificationActionType } from 'src/app/shared/enums/notification-action-type.enum';
import { HighFiveViewModalProxyService } from 'src/app/shared/modals/high-five-view-modal/high-five-view-modal-proxy.service';
import { iconColors } from '@app/shared/constants';

@Component({
  selector: 'a360-notification-item',
  templateUrl: './notification-item.component.html',
  styleUrls: ['./notification-item.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class NotificationItemComponent {
  @Input()
  public item: NotificationItemApiModel;

  @Input()
  @HostBinding('class.read')
  public isRead: NotificationItemApiModel;

  @Output() public removed = new EventEmitter<NotificationItemApiModel>();
  @Output() public clicked = new EventEmitter();

  public pending = false;
  public iconColors = iconColors;

  private readonly subscriptions = new Array<Subscription>();

  constructor(
    private readonly customToastersService: CustomToastersService,
    private readonly routeHelper: RouteHelperService,
    private readonly notificationsService: NavNotificationsService,
    private readonly highFiveViewModalProxyService: HighFiveViewModalProxyService,
    private readonly startQuizService: StartQuizService
  ) {}

  public onNotificationClick(): void {
    if (this.pending) {
      return;
    }

    this.pending = true;

    const subscription = this.item.IsRead ? of(null) : this.notificationsService.markAsRead(this.item.Id);

    subscription.subscribe(
      () => {
        const model = new NotificationStateAction(false, this.item.IsRead);
        if (!this.item.IsRead) {
          this.item.IsRead = true;
        }
        this.notificationsService.emitNotificationStateActionObserver(model);
        this.clicked.emit();
        this.pending = false;
        if (this.item.Action !== null) {
          if (this.item.Action.NotificationActionType === NotificationActionType.OpenHighFive) {
            this.highFiveViewModalProxyService.showModal(this.item.Action.TargetHighFiveId);

            return;
          }

          if (this.item.Action.NotificationActionType === NotificationActionType.TakeQuiz) {
            this.startQuizService.start(this.item.Action.TargetQuizInstanceId);

            return;
          }

          this.routeHelper.performNotificationAction(this.item.Id, this.item.Action);
        }
      },
      () => {
        this.handleError();
        this.customToastersService.failProcessing();
      }
    );
  }

  public deleteNotification(event: Event): void {
    event.stopPropagation();
    if (this.pending) {
      return;
    }

    this.pending = true;
    this.customToastersService.waitRemove();

    const subscription = this.notificationsService.delete(this.item.Id).subscribe(
      () => {
        this.removed.emit(this.item);
        this.notificationsService.emitNotificationStateActionObserver(
          new NotificationStateAction(true, this.item.IsRead)
        );
        this.pending = false;
        this.customToastersService.completeRemove();
      },
      () => {
        this.handleError();
        this.customToastersService.failRemove();
      }
    );

    this.subscriptions.push(subscription);
  }

  private handleError(): void {
    this.pending = false;
  }
}
